import type { ClassSummary, ImportResult, StudentSummary } from '@school-erp/types';
import { createStudentSchema } from '@school-erp/validation';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, ApiError, apiList, apiText } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  SchoolSelect,
  HiddenFieldErrors,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

const PAGE_SIZE = 10;
const STATUSES = ['active', 'transferred', 'graduated', 'alumni', 'inactive'] as const;
const GENDERS = ['male', 'female', 'other', 'undisclosed'] as const;

export function StudentsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId] = useState(
    () => new URLSearchParams(window.location.search).get('sectionId') ?? '',
  );
  const [panel, setPanel] = useState<'add' | 'import' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
    enabled: !!schoolId,
  });
  const students = useQuery({
    queryKey: ['students', { schoolId, page, q, status, classId, sectionId }],
    queryFn: ({ signal }) =>
      apiList<StudentSummary>('/students', {
        query: { schoolId, page, pageSize: PAGE_SIZE, q: q.trim(), status, classId, sectionId },
        signal,
      }),
    enabled: !!schoolId,
    placeholderData: keepPreviousData,
  });

  const exportCsv = useMutation({
    mutationFn: async () => {
      const csv = await apiText('/students/export', { query: { schoolId, status, classId } });
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'students.csv';
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['students'] });
  const totalPages = students.data?.meta.totalPages ?? 1;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('students.title')}</h1>
        <div className="flex flex-wrap gap-2">
          {can('students.export', schoolId) && (
            <button
              type="button"
              className={secondaryButton}
              disabled={!schoolId || exportCsv.isPending}
              onClick={() => exportCsv.mutate()}
            >
              {t('students.export')}
            </button>
          )}
          {can('students.import', schoolId) && (
            <button
              type="button"
              className={secondaryButton}
              aria-expanded={panel === 'import'}
              onClick={() => setPanel(panel === 'import' ? null : 'import')}
            >
              {t('students.import')}
            </button>
          )}
          {can('students.create', schoolId) && (
            <button
              type="button"
              className={primaryButton}
              aria-expanded={panel === 'add'}
              onClick={() => setPanel(panel === 'add' ? null : 'add')}
            >
              {t('students.add')}
            </button>
          )}
        </div>
      </div>

      <SchoolSelect
        value={schoolId}
        onChange={(id) => {
          setSchoolId(id);
          setClassId('');
          setPage(1);
        }}
      />

      {notice && <Notice kind="ok">{notice}</Notice>}
      {exportCsv.isError && <Notice kind="error">{errorMessage(exportCsv.error)}</Notice>}

      {panel === 'add' && schoolId && (
        <AddStudentForm
          schoolId={schoolId}
          classes={classes.data ?? []}
          onCreated={() => {
            setPanel(null);
            setNotice(t('students.created'));
            void refresh();
          }}
        />
      )}
      {panel === 'import' && schoolId && (
        <ImportPanel
          schoolId={schoolId}
          onImported={(count) => {
            setPanel(null);
            setNotice(t('students.importDone', { count }));
            void refresh();
          }}
        />
      )}

      <div className="flex flex-wrap gap-3">
        <div>
          <label htmlFor="student-search" className="block text-sm font-medium">
            {t('students.searchLabel')}
          </label>
          <input
            id="student-search"
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="mt-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="student-class" className="block text-sm font-medium">
            {t('students.class')}
          </label>
          <select
            id="student-class"
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value="">{t('students.allClasses')}</option>
            {classes.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="student-status" className="block text-sm font-medium">
            {t('adm.status')}
          </label>
          <select
            id="student-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value="">{t('students.allStatuses')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`students.status.${s}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {students.isPending && schoolId && <p role="status">{t('common.loading')}</p>}
      {students.isError && (
        <p role="alert" className="text-red-700">
          {t('common.loadError')}
        </p>
      )}
      {students.data && (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t('students.admissionNo')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('users.name')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('students.class')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('adm.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {students.data.data.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-600">
                      {t('students.empty')}
                    </td>
                  </tr>
                )}
                {students.data.data.map((s) => (
                  <tr key={s.id}>
                    <td className="px-4 py-3 font-mono text-xs">{s.admissionNumber}</td>
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/students/${s.id}`} className="text-brand-700 underline">
                        {s.firstName} {s.lastName}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      {s.enrollment
                        ? `${s.enrollment.className}${s.enrollment.sectionName ? ` – ${s.enrollment.sectionName}` : ''}`
                        : t('students.notEnrolled')}
                    </td>
                    <td className="px-4 py-3">{t(`students.status.${s.status}` as MessageKey)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} totalPages={totalPages} onPage={setPage} />
        </>
      )}
    </div>
  );
}

function AddStudentForm({
  schoolId,
  classes,
  onCreated,
}: {
  schoolId: string;
  classes: ClassSummary[];
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'female',
    classId: '',
    sectionId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const sections = classes.find((c) => c.id === form.classId)?.sections ?? [];

  const create = useMutation({
    mutationFn: ({ body, allowDuplicate }: { body: object; allowDuplicate: boolean }) =>
      api('/students', {
        method: 'POST',
        body,
        query: { allowDuplicate: allowDuplicate ? 'true' : undefined },
      }),
    onSuccess: onCreated,
  });

  async function submit(allowDuplicate: boolean) {
    setMessage(null);
    const parsed = createStudentSchema.safeParse({
      schoolId,
      firstName: form.firstName,
      lastName: form.lastName,
      dateOfBirth: form.dateOfBirth,
      gender: form.gender,
      ...(form.classId && {
        enrollment: { classId: form.classId, ...(form.sectionId && { sectionId: form.sectionId }) },
      }),
    });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    try {
      await create.mutateAsync({ body: parsed.data, allowDuplicate });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'POSSIBLE_DUPLICATE_STUDENT') {
        if (window.confirm(t('students.duplicate'))) await submit(true);
      } else {
        setMessage(errorMessage(error));
      }
    }
  }

  const bind = (name: 'firstName' | 'lastName' | 'dateOfBirth') => ({
    value: form[name],
    error: errors[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [name]: e.target.value }),
  });

  return (
    <form
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void submit(false);
      }}
      className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
    >
      <TextField label={t('students.firstName')} required {...bind('firstName')} />
      <TextField label={t('students.lastName')} required {...bind('lastName')} />
      <TextField label={t('students.dob')} type="date" required {...bind('dateOfBirth')} />
      <div>
        <label htmlFor="new-gender" className="block text-sm font-medium">
          {t('students.gender')}
        </label>
        <select
          id="new-gender"
          value={form.gender}
          onChange={(e) => setForm({ ...form, gender: e.target.value })}
          className={selectClass}
        >
          {GENDERS.map((g) => (
            <option key={g} value={g}>
              {t(`students.gender.${g}` as MessageKey)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="new-class" className="block text-sm font-medium">
          {t('students.class')}
        </label>
        <select
          id="new-class"
          value={form.classId}
          onChange={(e) => setForm({ ...form, classId: e.target.value, sectionId: '' })}
          className={selectClass}
        >
          <option value="">{t('students.notEnrolled')}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="new-section" className="block text-sm font-medium">
          {t('students.section')}
        </label>
        <select
          id="new-section"
          value={form.sectionId}
          disabled={!form.classId}
          onChange={(e) => setForm({ ...form, sectionId: e.target.value })}
          className={selectClass}
        >
          <option value="">{t('students.noSection')}</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2 sm:col-span-3">
        {message && <Notice kind="error">{message}</Notice>}
        <HiddenFieldErrors errors={errors} visible={['firstName', 'lastName', 'dateOfBirth']} />
        <button type="submit" disabled={create.isPending} className={primaryButton}>
          {create.isPending ? t('common.saving') : t('students.create')}
        </button>
      </div>
    </form>
  );
}

function ImportPanel({
  schoolId,
  onImported,
}: {
  schoolId: string;
  onImported: (count: number) => void;
}) {
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<ImportResult>('/students/import', { method: 'POST', body: { schoolId, csv, dryRun } }),
    onSuccess: (data) => {
      setError(null);
      setResult(data);
      if (!data.dryRun && data.errors.length === 0) onImported(data.created);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <section
      aria-label={t('students.import')}
      className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
    >
      <p className="text-sm text-slate-600">{t('students.importHelp')}</p>
      <div>
        <label htmlFor="import-file" className="block text-sm font-medium">
          {t('students.importFile')}
        </label>
        <input
          id="import-file"
          type="file"
          accept=".csv,text/csv"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            setResult(null);
            setCsv(file ? await file.text() : '');
          }}
          className="mt-1 text-sm"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className={secondaryButton}
          disabled={!csv || run.isPending}
          onClick={() => run.mutate(true)}
        >
          {t('students.validate')}
        </button>
        <button
          type="button"
          className={primaryButton}
          disabled={!csv || run.isPending}
          onClick={() => run.mutate(false)}
        >
          {t('students.runImport')}
        </button>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {result && result.errors.length === 0 && result.dryRun && (
        <Notice kind="ok">{t('students.importOk', { count: result.created })}</Notice>
      )}
      {result && result.errors.length > 0 && (
        <div role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">
          <p className="font-medium">{t('students.importErrors')}</p>
          <ul className="mt-1 list-disc pl-5">
            {result.errors.map((e, i) => (
              <li key={i}>
                {t('students.row', { row: e.row })}
                {e.field ? ` (${e.field})` : ''}: {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
